import React, { useState, useEffect, useCallback } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { cn } from '../utils/cn';
import { api } from '../lib/api';

const { FiShield, FiKey, FiCopy, FiArrowRight, FiDownload, FiAlertTriangle, FiCheckCircle, FiEye, FiTrash2, FiLoader, FiExternalLink, FiRepeat, FiRefreshCw } = FiIcons;

const WSOL = 'So11111111111111111111111111111111111111112';

export default function Wallet({ wallet, onWalletChange }) {
  const [pkInput, setPkInput] = useState('');
  const [destAddress, setDestAddress] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [busy, setBusy] = useState('');
  const [status, setStatus] = useState({ type: '', msg: '' });
  const [revealed, setRevealed] = useState('');

  // Holdings + swap
  const [tokens, setTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [fromMint, setFromMint] = useState(WSOL);
  const [toMode, setToMode] = useState('SOL'); // 'SOL' | 'CUSTOM'
  const [toMint, setToMint] = useState('');
  const [swapAmount, setSwapAmount] = useState('');
  const [quote, setQuote] = useState(null);

  const showStatus = (type, msg) => {
    setStatus({ type, msg });
    setTimeout(() => setStatus({ type: '', msg: '' }), 6000);
  };

  const loadTokens = useCallback(async () => {
    if (!wallet?.pubkey) return;
    setTokensLoading(true);
    try {
      const t = await api.getTokens();
      setTokens(Array.isArray(t) ? t : []);
    } catch (e) {
      console.error('load tokens failed', e);
    } finally {
      setTokensLoading(false);
    }
  }, [wallet?.pubkey]);

  useEffect(() => { loadTokens(); }, [loadTokens]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try {
      await fn();
      if (ok) showStatus('success', ok);
      if (onWalletChange) await onWalletChange();
    } catch (e) {
      showStatus('error', e.message || 'Operation failed');
    } finally {
      setBusy('');
    }
  };

  const handleGenerate = () => run('gen', () => api.createWallet(), 'New burner generated on the engine.');
  const handleImport = () => run('import', async () => { await api.importWallet(pkInput.trim()); setPkInput(''); }, 'Wallet imported.');
  const handleWithdraw = () => run('withdraw', async () => {
    const { txid } = await api.withdraw(destAddress, parseFloat(withdrawAmount));
    setWithdrawAmount('');
    showStatus('success', `Withdrawal sent: ${txid.substring(0, 8)}...`);
  });
  const handleReveal = async () => {
    if (!window.confirm('Reveal the burner secret key? Anyone with it controls the funds.')) return;
    setBusy('reveal');
    try {
      const { secret } = await api.revealWallet();
      setRevealed(secret || '');
    } catch (e) {
      showStatus('error', e.message);
    } finally {
      setBusy('');
    }
  };
  const handleRemove = () => {
    if (!window.confirm('Remove the burner from the engine? Save the secret first if you need it.')) return;
    run('remove', () => api.deleteWallet(), 'Burner removed.');
  };

  const copyAddress = () => {
    if (wallet?.pubkey) {
      navigator.clipboard.writeText(wallet.pubkey);
      showStatus('success', 'Address copied.');
    }
  };

  // ---- Swap helpers ----
  const resolvedToMint = toMode === 'SOL' ? WSOL : toMint.trim();
  const fromToken = tokens.find((t) => t.mint === fromMint);
  const fromDecimals = fromMint === WSOL ? 9 : (fromToken?.decimals ?? 9);

  const toRaw = () => {
    const a = parseFloat(swapAmount);
    if (!Number.isFinite(a) || a <= 0) return null;
    let raw = BigInt(Math.floor(a * Math.pow(10, fromDecimals)));
    if (fromToken && BigInt(fromToken.raw) < raw) raw = BigInt(fromToken.raw); // cap at balance
    return raw > 0n ? raw.toString() : null;
  };

  const setMaxAmount = () => {
    if (fromMint === WSOL) setSwapAmount(Math.max(0, (wallet?.balance ?? 0) - 0.01).toFixed(4));
    else if (fromToken) setSwapAmount(String(fromToken.amount));
  };

  const prefillSellToSol = (token) => {
    setFromMint(token.mint);
    setToMode('SOL');
    setToMint('');
    setSwapAmount(String(token.amount));
    setQuote(null);
  };

  const fmt = (raw, decimals) => (Number(raw) / Math.pow(10, decimals));

  const handleGetQuote = async () => {
    const amountRaw = toRaw();
    if (!amountRaw || !resolvedToMint) { showStatus('error', 'Enter an amount and a valid destination.'); return; }
    if (resolvedToMint === fromMint) { showStatus('error', 'From and To must differ.'); return; }
    setBusy('quote'); setQuote(null);
    try {
      const q = await api.swapQuote(fromMint, resolvedToMint, amountRaw);
      setQuote(q);
    } catch (e) {
      showStatus('error', e.message || 'Quote failed');
    } finally { setBusy(''); }
  };

  const handleSwap = async () => {
    const amountRaw = toRaw();
    if (!amountRaw || !resolvedToMint) { showStatus('error', 'Enter an amount and a valid destination.'); return; }
    if (resolvedToMint === fromMint) { showStatus('error', 'From and To must differ.'); return; }
    setBusy('swap');
    try {
      const { txid } = await api.swap(fromMint, resolvedToMint, amountRaw);
      showStatus('success', `Swap sent: ${String(txid).substring(0, 8)}...`);
      setSwapAmount(''); setQuote(null);
      await loadTokens();
      if (onWalletChange) await onWalletChange();
    } catch (e) {
      showStatus('error', e.message || 'Swap failed');
    } finally { setBusy(''); }
  };

  if (!wallet) {
    return (
      <div className="h-full overflow-y-auto custom-scrollbar pr-2">
        <div className="max-w-xl mx-auto mt-12 glass-panel p-8 rounded-2xl text-center space-y-6">
          <div className="w-16 h-16 bg-purple-500/10 rounded-full flex items-center justify-center mx-auto border border-purple-500/20">
            <SafeIcon icon={FiShield} className="text-3xl text-purple-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">Burner Wallet</h2>
            <p className="text-slate-400 text-sm leading-relaxed">
              The burner is held <span className="text-purple-300 font-bold">encrypted on the engine</span>, which signs trades 24/7.
              <span className="text-red-400 block mt-2 font-bold flex items-center justify-center gap-1">
                <SafeIcon icon={FiAlertTriangle} /> Keep only small amounts here.
              </span>
            </p>
          </div>

          {status.msg && (
            <div className={cn('p-3 rounded-lg text-xs font-bold', status.type === 'error' ? 'bg-red-500/10 text-red-400' : 'bg-green-500/10 text-green-400')}>
              {status.msg}
            </div>
          )}

          <div className="space-y-4 pt-6 border-t border-slate-800">
            <button onClick={handleGenerate} disabled={busy} className="btn-primary w-full flex items-center justify-center gap-2">
              {busy === 'gen' ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiKey} />} Generate New Burner
            </button>
            <div className="relative flex items-center py-2">
              <div className="flex-grow border-t border-slate-800" />
              <span className="flex-shrink-0 mx-4 text-slate-500 text-xs font-bold uppercase">or import</span>
              <div className="flex-grow border-t border-slate-800" />
            </div>
            <div className="flex gap-2">
              <input type="password" placeholder="Paste Base58 Private Key" className="input-field flex-1" value={pkInput} onChange={(e) => setPkInput(e.target.value)} />
              <button onClick={handleImport} disabled={busy || !pkInput} className="bg-slate-800 hover:bg-slate-700 text-white font-bold px-6 rounded-lg transition-all border border-slate-700 disabled:opacity-50">
                {busy === 'import' ? '...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto custom-scrollbar pr-2">
      <div className="max-w-3xl mx-auto space-y-6 pb-12">
        <div className="glass-panel p-6 rounded-2xl flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center shadow-lg">
              <SafeIcon icon={FiShield} className="text-white text-xl" />
            </div>
            <div>
              <div className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">Engine Burner</div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-white bg-slate-900/50 px-3 py-1 rounded-lg border border-slate-800">
                  {wallet.pubkey.substring(0, 6)}...{wallet.pubkey.substring(wallet.pubkey.length - 6)}
                </span>
                <button onClick={copyAddress} className="text-slate-400 hover:text-white p-1 rounded-md hover:bg-slate-800 transition-colors">
                  <SafeIcon icon={FiCopy} />
                </button>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">Balance</div>
            {wallet.balanceError ? (
              <div className="text-sm font-bold text-amber-400 flex items-center gap-1.5 justify-end">
                <SafeIcon icon={FiAlertTriangle} />
                Unable to fetch — RPC unavailable
              </div>
            ) : (
              <div className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">
                {(wallet.balance ?? 0).toFixed(4)} SOL
              </div>
            )}
          </div>
        </div>

        {status.msg && (
          <div className={cn('p-4 rounded-xl text-sm font-bold flex items-center gap-2',
            status.type === 'error' ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-green-500/10 text-green-400 border border-green-500/20')}>
            <SafeIcon icon={status.type === 'error' ? FiAlertTriangle : FiCheckCircle} />
            {status.msg}
          </div>
        )}

        {/* Holdings */}
        <div className="glass-panel rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Token Holdings</h3>
            <button onClick={loadTokens} disabled={tokensLoading} className="text-slate-400 hover:text-white p-1.5 rounded-md hover:bg-slate-800 transition-colors" title="Refresh">
              <SafeIcon icon={tokensLoading ? FiLoader : FiRefreshCw} className={tokensLoading ? 'animate-spin' : ''} />
            </button>
          </div>

          {/* SOL row */}
          <div className="flex items-center justify-between bg-[#0B0E14] rounded-lg p-3 border border-slate-800">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm text-white">SOL</span>
              <span className="text-[10px] text-slate-500 font-mono">native</span>
            </div>
            <div className="font-mono text-sm text-slate-200">
              {wallet.balanceError ? <span className="text-amber-400 text-xs">unavailable</span> : (wallet.balance ?? 0).toFixed(4)}
            </div>
          </div>

          {tokens.length === 0 ? (
            <p className="text-xs text-slate-500">{tokensLoading ? 'Loading holdings…' : 'No SPL tokens held.'}</p>
          ) : (
            tokens.map((t) => (
              <div key={t.mint} className="flex items-center justify-between bg-[#0B0E14] rounded-lg p-3 border border-slate-800">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-white">{t.symbol}</span>
                    <a href={`https://dexscreener.com/solana/${t.mint}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300" title="View on DexScreener">
                      <SafeIcon icon={FiExternalLink} className="text-xs" />
                    </a>
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono mt-0.5">{t.mint.slice(0, 6)}…{t.mint.slice(-4)}</div>
                </div>
                <div className="text-right flex items-center gap-3">
                  <div>
                    <div className="font-mono text-sm text-slate-200">{Number(t.amount).toLocaleString(undefined, { maximumFractionDigits: 4 })}</div>
                    <div className="text-[10px] text-slate-500">{t.usd != null ? `$${t.usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '—'}</div>
                  </div>
                  <button onClick={() => prefillSellToSol(t)} className="text-[10px] font-bold uppercase bg-purple-500/10 hover:bg-purple-500/20 text-purple-300 border border-purple-500/20 px-2 py-1.5 rounded transition-colors whitespace-nowrap">
                    Swap → SOL
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Swap */}
        <div className="glass-panel rounded-2xl p-6 space-y-4">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
            <SafeIcon icon={FiRepeat} className="text-purple-400" /> Swap
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-500 uppercase">From</label>
              <select value={fromMint} onChange={(e) => { setFromMint(e.target.value); setQuote(null); }} className="input-field text-sm appearance-none cursor-pointer">
                <option value={WSOL}>SOL ({(wallet.balance ?? 0).toFixed(3)})</option>
                {tokens.map((t) => (
                  <option key={t.mint} value={t.mint}>{t.symbol} ({Number(t.amount).toLocaleString(undefined, { maximumFractionDigits: 3 })})</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-500 uppercase">To</label>
              <select value={toMode} onChange={(e) => { setToMode(e.target.value); setQuote(null); }} className="input-field text-sm appearance-none cursor-pointer">
                <option value="SOL">SOL</option>
                <option value="CUSTOM">Token (paste mint)…</option>
              </select>
            </div>
          </div>

          {toMode === 'CUSTOM' && (
            <input type="text" value={toMint} onChange={(e) => { setToMint(e.target.value); setQuote(null); }} placeholder="Destination token mint address" className="input-field text-sm" />
          )}

          <div className="space-y-2">
            <div className="flex justify-between items-end">
              <label className="text-[10px] font-bold text-slate-500 uppercase">Amount ({fromMint === WSOL ? 'SOL' : (fromToken?.symbol || 'token')})</label>
              <button onClick={setMaxAmount} className="text-xs text-blue-400 hover:text-blue-300 font-bold">MAX</button>
            </div>
            <input type="number" value={swapAmount} onChange={(e) => { setSwapAmount(e.target.value); setQuote(null); }} placeholder="0.00" className="input-field text-lg font-bold" />
          </div>

          {quote && (
            <div className="bg-[#0B0E14] border border-slate-800 rounded-lg p-3 text-xs space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">Expected output</span><span className="font-mono text-green-400">≈ {fmt(quote.outAmount, quote.outDecimals).toLocaleString(undefined, { maximumFractionDigits: 6 })} {quote.outSymbol}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Price impact</span><span className={cn('font-mono', parseFloat(quote.priceImpactPct) * 100 > 5 ? 'text-red-400' : 'text-slate-300')}>{(parseFloat(quote.priceImpactPct) * 100).toFixed(2)}%</span></div>
            </div>
          )}

          <div className="flex gap-2">
            <button onClick={handleGetQuote} disabled={busy || !swapAmount} className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold py-3 px-4 rounded-lg transition-all border border-slate-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {busy === 'quote' ? <SafeIcon icon={FiLoader} className="animate-spin" /> : 'Get Quote'}
            </button>
            <button onClick={handleSwap} disabled={busy || !swapAmount} className="flex-1 btn-primary flex items-center justify-center gap-2">
              {busy === 'swap' ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <><SafeIcon icon={FiRepeat} /> Swap</>}
            </button>
          </div>
        </div>

        {/* Withdraw */}
        <div className="glass-panel rounded-2xl p-6 space-y-4">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
            <SafeIcon icon={FiDownload} className="text-blue-400" /> Withdraw SOL
          </h3>
          <input type="text" value={destAddress} onChange={(e) => setDestAddress(e.target.value)} placeholder="Destination Solana address..." className="input-field" />
          <div className="flex justify-between items-end">
            <label className="text-xs font-bold text-slate-500 uppercase">Amount (SOL)</label>
            <button onClick={() => setWithdrawAmount(((wallet.balance ?? 0) - 0.005).toFixed(4))} className="text-xs text-blue-400 hover:text-blue-300 font-bold">MAX</button>
          </div>
          <input type="number" value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} placeholder="0.00" className="input-field text-lg font-bold" />
          <button onClick={handleWithdraw} disabled={busy || !withdrawAmount || !destAddress}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold py-3 px-4 rounded-lg transition-all shadow-lg shadow-blue-500/20 disabled:opacity-50 flex items-center justify-center gap-2">
            {busy === 'withdraw' ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <><SafeIcon icon={FiArrowRight} /> Send Funds</>}
          </button>
        </div>

        {/* Danger zone */}
        <div className="glass-panel rounded-2xl p-6 space-y-4 border border-red-500/10">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider">Key Management</h3>
          <div className="flex flex-wrap gap-3">
            <button onClick={handleReveal} disabled={busy} className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 px-4 py-2 rounded-lg text-sm font-bold disabled:opacity-50">
              <SafeIcon icon={FiEye} /> Reveal Secret
            </button>
            <button onClick={handleRemove} disabled={busy} className="flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 px-4 py-2 rounded-lg text-sm font-bold disabled:opacity-50">
              <SafeIcon icon={FiTrash2} /> Remove Burner
            </button>
          </div>
          {revealed && (
            <div className="bg-[#0B0E14] border border-red-500/30 rounded-lg p-3">
              <div className="text-[10px] text-red-400 font-bold uppercase mb-1">Secret key (base58) — copy and store safely, then clear</div>
              <div className="font-mono text-xs text-slate-300 break-all">{revealed}</div>
              <button onClick={() => setRevealed('')} className="mt-2 text-[10px] text-slate-400 hover:text-white font-bold uppercase">Hide</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
